/** Pre-trip item → General tab. Name, category (remote autocomplete carrying
 *  the category's rendering_type), note, the link/file/text display-mode
 *  RadioGroup, and access tags (remote multi-select scoped to the item's
 *  tenant). Items have no optimistic-concurrency token, so there is no conflict
 *  banner (last-writer-wins, like regions).
 *
 *  MODE RESOLUTION (the crux): an item is displayed as EXACTLY ONE of:
 *   - link → `{ url, deep_link, text: null, attachment: null }`
 *   - file → multipart `{ url: null, text: null }` + the `attachment` File
 *            (only when a NEW File is staged; if the file is unchanged we send
 *            neither the part nor `attachment: null`, so the existing file
 *            stays). A cleared file sends `attachment: null` and no File.
 *   - text → `{ text, url: null, attachment: null }`
 *  The text option is HIDDEN when the picked category's `rendering_type === 2`
 *  ("List with checkboxes"); if the current record is in text mode and the user
 *  switches to such a category, the mode falls back to link. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Checkbox, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { rbacApi, type AccessTag } from '@/api/rbac';
import type { DisplayMode, ItemTypeBriefRef, PreTripItemDetail, PreTripItemWriteJson } from '@/api/preTripItems';
import * as typesApi from '@/api/preTripItemTypes';
import type { PreTripItemTypeRow } from '@/api/preTripItemTypes';
import { AttachmentUploadField, type AttachmentFieldValue } from '@/components/AttachmentUploadField/AttachmentUploadField';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { QuillEditor } from '@/components/RichText/QuillEditor';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { usePermissions } from '@/hooks/usePermissions';

import { preTripCategoryActions, webUrlActions, type CatalogCtx } from '../../catalog/catalogLinks';
import { usePreTripItemPatch } from '../hooks/usePreTripItemPatch';

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  note: z.string().nullable(),
  url: z.string().nullable(),
});

type FormValues = z.infer<typeof Schema>;

function toDefaults(i: PreTripItemDetail): FormValues {
  return {
    name: i.name,
    note: i.note ?? '',
    url: i.url ?? '',
  };
}

/** The category picker deals in the full row; the item carries a brief ref.
 *  Coerce the brief ref into a minimally-shaped row for the picker's value. */
function seedCategory(ref: ItemTypeBriefRef | null): PreTripItemTypeRow | null {
  if (!ref) return null;
  return {
    id: ref.id,
    tenant: { id: 0, code: '', name: '', is_franchise: false, parent_id: null },
    name: ref.name,
    system_name: '',
    rendering_type: ref.rendering_type,
    rendering_type_display: null,
    item_count: 0,
    is_system: false,
  };
}

const ids = (xs: { id: number }[]) => xs.map((x) => x.id).sort((a, b) => a - b);
const sameIds = (a: { id: number }[], b: { id: number }[]) =>
  a.length === b.length && ids(a).every((v, i) => v === ids(b)[i]);

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function PreTripItemGeneralTab({ item, onSaved }: { item: PreTripItemDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_pretrip_manage');
  // Category + access-tag pickers are tour-operator-scoped; an unassigned item
  // (null tenant) has no operator to scope them to. Local const so the narrowing
  // flows into the async queryFn closures below.
  const tenantId = item.tenant?.id ?? null;
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(item),
  });
  const patch = usePreTripItemPatch(item, onSaved);

  const seedAtags = (xs: { id: number; name: string | null }[]): AccessTag[] =>
    xs.map((a) => ({ id: a.id, name: a.name ?? '' }));

  const [category, setCategory] = useState<PreTripItemTypeRow | null>(() => seedCategory(item.item_type));
  const [mode, setMode] = useState<DisplayMode>(item.display_mode);
  const [deepLink, setDeepLink] = useState(item.deep_link);
  const [attachment, setAttachment] = useState<AttachmentFieldValue>(null);
  const [text, setText] = useState(item.text ?? '');
  const [atags, setAtags] = useState<AccessTag[]>(() => seedAtags(item.atag));

  const resetToRecord = () => {
    reset(toDefaults(item));
    setCategory(seedCategory(item.item_type));
    setMode(item.display_mode);
    setDeepLink(item.deep_link);
    setAttachment(null);
    setText(item.text ?? '');
    setAtags(seedAtags(item.atag));
  };
  useEffect(() => {
    resetToRecord();
    // Re-seed whenever the server record changes (incl. after a save → refetch, so
    // the form isn't stuck "dirty"). Items have no updated_ts; React Query hands us a
    // fresh top-level ref only when a field actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Text mode is not available for "List with checkboxes" categories.
  const textAllowed = category?.rendering_type !== 2;
  const effectiveMode: DisplayMode = mode === 'text' && !textAllowed ? 'link' : mode;

  const extrasDirty = useMemo(() => {
    if (category?.id !== item.item_type?.id) return true;
    if (effectiveMode !== item.display_mode) return true;
    if (!sameIds(atags, item.atag)) return true;
    if (effectiveMode === 'link' && deepLink !== item.deep_link) return true;
    if (effectiveMode === 'file' && (attachment instanceof File || attachment === 'cleared')) return true;
    if (effectiveMode === 'text' && (text || '') !== (item.text ?? '')) return true;
    return false;
  }, [category, effectiveMode, atags, deepLink, attachment, text, item]);

  const submit = handleSubmit((v) => {
    const body: PreTripItemWriteJson = {
      name: v.name.trim(),
      note: v.note?.trim() || null,
      atag: atags.map((a) => ({ id: a.id })),
    };
    if (category) body.item_type = category.id;

    let file: File | null = null;
    if (effectiveMode === 'link') {
      body.url = v.url?.trim() || null;
      body.deep_link = deepLink;
      body.text = null;
      body.attachment = null;
    } else if (effectiveMode === 'text') {
      body.text = text || null;
      body.url = null;
      body.attachment = null;
    } else {
      // file: keep the existing file unless a new one is staged or it was cleared.
      body.url = null;
      body.text = null;
      if (attachment instanceof File) file = attachment;
      else if (attachment === 'cleared') body.attachment = null;
    }

    patch.save(body, file);
  });

  const err = formState.errors;
  const dirty = formState.isDirty || extrasDirty;
  const ctx: CatalogCtx = { tenantId };

  return (
    <form onSubmit={submit}>
      <Section title="Basics">
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Name" error={!!err.name} helperText={err.name?.message} />
        )} />
        {tenantId != null ? (
          <RemoteAutocomplete<PreTripItemTypeRow>
            label="Category" size="small" value={category}
            getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
            fetchOnEmpty
            queryFn={async (q) =>
              (await typesApi.list({ tour_operator_id: tenantId, search: q || undefined, pageSize: 50 })).results
            }
            onChange={(v) => setCategory((v as PreTripItemTypeRow | null) ?? null)}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Category can’t be assigned — this item has no owning operator.
          </Typography>
        )}
        {/* The picker keeps its own click; the picked category's links live
            in the caption row underneath. */}
        <PickerLinks
          items={
            category
              ? [{ key: category.id, label: category.name, actions: preTripCategoryActions(category, ctx) }]
              : []
          }
        />
        <Controller control={control} name="note" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth multiline minRows={2} sx={{ mt: 1.5 }} disabled={!canManage}
            label="Note" error={!!err.note} helperText={err.note?.message} />
        )} />
      </Section>

      <Section title="Content">
        <RadioGroup row value={effectiveMode} onChange={(e) => setMode(e.target.value as DisplayMode)} sx={{ mb: 1 }}>
          <FormControlLabel value="link" control={<Radio size="small" disabled={!canManage} />} label="Link" />
          <FormControlLabel value="file" control={<Radio size="small" disabled={!canManage} />} label="File" />
          {textAllowed && (
            <FormControlLabel value="text" control={<Radio size="small" disabled={!canManage} />} label="Text" />
          )}
        </RadioGroup>

        {effectiveMode === 'link' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Controller control={control} name="url" render={({ field }) => (
              <TextField {...field} value={field.value ?? ''} size="small" fullWidth disabled={!canManage}
                label="URL" error={!!err.url} helperText={err.url?.message}
                InputProps={{
                  endAdornment: (
                    <FieldActionsAdornment
                      label="Open the URL"
                      actions={webUrlActions(field.value, 'Open the URL')}
                    />
                  ),
                }} />
            )} />
            <FormControlLabel
              control={<Checkbox checked={deepLink} onChange={(e) => setDeepLink(e.target.checked)} size="small" disabled={!canManage} />}
              label="Open in Safari (instead of the in-app browser)"
            />
          </Box>
        )}
        {effectiveMode === 'file' && (
          <>
            <AttachmentUploadField
              label="Attachment"
              currentUrl={item.attachment?.url ?? null}
              currentName={item.attachment?.name ?? null}
              value={attachment} onChange={setAttachment} canManage={canManage}
            />
            {/* The widget links the file; the URL itself is what gets pasted
                into a ticket, so it copies too. */}
            <PickerLinks
              items={
                item.attachment?.url
                  ? [{
                      key: 'attachment',
                      label: item.attachment.name ?? 'Attachment',
                      actions: webUrlActions(item.attachment.url, 'Open the attachment'),
                    }]
                  : []
              }
            />
          </>
        )}
        {effectiveMode === 'text' && (
          <QuillEditor label="Text" value={text} onChange={setText} readOnly={!canManage} />
        )}
      </Section>

      <Section title="Access">
        {tenantId != null ? (
          <RemoteAutocomplete<AccessTag>
            label="Access tags" size="small" multiple fetchOnEmpty value={atags}
            getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
            queryFn={async (q) => (await rbacApi.listAccessTags(tenantId, q)).results}
            onChange={(v) => setAtags((v as AccessTag[]) ?? [])}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Access tags are unavailable — this item has no owning operator.
          </Typography>
        )}
      </Section>

      <EditorSaveBar
        dirty={dirty} saving={patch.saving} canManage={canManage}
        onSave={submit} onDiscard={resetToRecord}
      />
    </form>
  );
}
